"use client";

import {
  SYNC_WAVES,
  usePendingSyncResources,
  useSyncStatusMany,
} from "@stockline/web";
import type {
  BootSyncSnapshot,
  StepState,
  WaveKey,
  WaveSnapshot,
} from "./types";

/**
 * Map (predecessor-ready, this-ready) → step state. Centralizes the
 * stepper-state transitions so consumers don't need nested ternaries
 * for each wave.
 */
export function deriveStepState(
  predecessorReady: boolean,
  thisReady: boolean,
): StepState {
  if (!predecessorReady) return "pending";
  if (thisReady) return "completed";
  return "active";
}

/** Active wave for cycling labels: first wave that isn't done yet. */
function deriveActiveWave(criticalReady: boolean, warmReady: boolean): WaveKey {
  if (criticalReady && warmReady) return "background";
  if (criticalReady) return "warm";
  return "critical";
}

/**
 * Reads every signal the boot screen needs (per-wave status + pending
 * resource lists) and packages them into a single snapshot. Pulling
 * this into one hook keeps the main `GlobalBootScreen` component below
 * SonarCloud's cognitive-complexity threshold.
 */
export function useBootSyncSnapshot(): BootSyncSnapshot {
  const criticalStatus = useSyncStatusMany(SYNC_WAVES.critical);
  const warmStatus = useSyncStatusMany(SYNC_WAVES.warm);
  const backgroundStatus = useSyncStatusMany(SYNC_WAVES.background);
  const criticalPending = usePendingSyncResources(SYNC_WAVES.critical);
  const warmPending = usePendingSyncResources(SYNC_WAVES.warm);
  const backgroundPending = usePendingSyncResources(SYNC_WAVES.background);

  const criticalReady = criticalStatus === "ready";
  const warmReady = warmStatus === "ready";
  const backgroundReady = backgroundStatus === "ready";

  const critical: WaveSnapshot = {
    state: deriveStepState(true, criticalReady),
    totalCount: SYNC_WAVES.critical.length,
    readyCount: SYNC_WAVES.critical.length - criticalPending.length,
  };
  const warm: WaveSnapshot = {
    state: deriveStepState(criticalReady, warmReady),
    totalCount: SYNC_WAVES.warm.length,
    readyCount: SYNC_WAVES.warm.length - warmPending.length,
  };
  const background: WaveSnapshot = {
    state: deriveStepState(criticalReady && warmReady, backgroundReady),
    totalCount: SYNC_WAVES.background.length,
    readyCount: SYNC_WAVES.background.length - backgroundPending.length,
  };

  const activeWave = deriveActiveWave(criticalReady, warmReady);

  return {
    critical,
    warm,
    background,
    activeWaveResources: SYNC_WAVES[activeWave],
    criticalReady,
    warmReady,
    warmPending,
    backgroundPending,
    allReady: criticalReady && warmReady && backgroundReady,
  };
}
